import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Card, Col, Container, Row, Table } from "react-bootstrap"
import { useWallet } from "@solana/wallet-adapter-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDarknetIdentity } from "../contexts/IdentityContext";

import ApiService from "../services/ApiService";
import { getGameAssetsUrl, getProfilePicture, shortenAddress } from "../utilities/Helpers";
import EditProfileModal from "../components/modals/EditProfileModal";
import CustomBreadcrumbs from "../components/common/CustomBreadcrumbs";
import CustomTooltip from "../components/common/CustomTooltip";

import solanaIcon from '../styles/images/solana.png';

const ProfilePage = () => {
    const navigate = useNavigate();
    const { profileAddress } = useParams();
    const { publicKey } = useWallet();
    const { identity } = useDarknetIdentity();
    const [address, setAddress] = useState<string>();
    const [profile, setProfile] = useState<Profile>();
    const [records, setRecords] = useState<GameRecord[]>([]);

    useEffect(() => {
        let publicAddress = profileAddress ?? publicKey?.toBase58();
        if (publicAddress) {
            setAddress(publicAddress);
            ApiService.get(`/account/${publicAddress}`).then((response) => {
                if (!response) throw new Error("No response");
                if (!response.success) throw new Error(response.message);
                setProfile(response.data);
            }).catch((error) => {
                navigate('/');
            });

            ApiService.get(`/account/${publicAddress}/records`).then((response) => {
                if (!response) throw new Error("No response");
                if (!response.success) throw new Error(response.message);
                setRecords(response.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [navigate, profileAddress, publicKey]);

    const Profile = () => {
        if (!profile || !records) return (<>Loading profile...</>);

        let wins = records.filter(gr => gr.winner.address === address).length;
        let losses = records.filter(gr => gr.winner.address !== address).length;
        let ratio = (wins / (records.length ?? 1)).toFixed(2);

        return (
            <Row>
                <Col xs={12} md={2} className="text-center text-light">
                    <div className="profile-picture">
                        <img src={getProfilePicture(profile.address)} alt="pfp" height={136} />
                    </div>
                    <br />
                    <h4>{profile.name}</h4>
                    <div className="pt-2">
                        <Row className="mt-2 justify-content-between">
                            <Col className="text-start">Wins:</Col>
                            <Col className="text-end">{wins}</Col>
                        </Row>
                        <Row className="mt-2 justify-content-between">
                            <Col className="text-start">Losses:</Col>
                            <Col className="text-end">{losses}</Col>
                        </Row>
                        <Row className="mt-2 justify-content-between">
                            <Col className="text-start">W/L Ratio:</Col>
                            <Col className="text-end">{ratio}</Col>
                        </Row>
                    </div>
                </Col>
                <Col xs={12} md={9}>
                    <hr className="d-block d-md-none mt-3" />
                    <div className="vr h-75 position-absolute d-none d-md-inline-block"></div>
                    <div className="ps-md-4">
                        <h4>Game History</h4>
                        <Table className="text-center">
                            <thead>
                                <tr>
                                    <th>Player 1</th>
                                    <th>Player 2</th>
                                    <th className="d-none d-md-table-cell">Game Played</th>
                                    <th>Record Date</th>
                                    <th className="d-none d-md-table-cell">Winner</th>
                                </tr>
                            </thead>
                            <tbody>
                                <GameRecords />
                            </tbody>
                        </Table>
                    </div>
                </Col>
            </Row>
        )
    }

    const GameRecords = () => {
        if (!records) return <li key="loading">Loading game history...</li>
        if (records.length === 0) return <li key="empty">No game history</li>;

        return <>{records.map((record) => {
            return (
                <tr key={record.id}>
                    <td>
                        <Link to={`/profile/${record.player1.address}`} className={(record.winner.name === record.player1.name) ? "text-success" : "text-light"}>{record.player1.name}</Link>
                        <span className='d-none d-md-inline-block'>&nbsp;- {record.player1.score} pts</span>
                    </td>
                    <td>
                        <Link to={`/profile/${record.player2.address}`} className={(record.winner.name === record.player2.name) ? "text-success" : "text-light"}>{record.player2.name}</Link>
                        <span className='d-none d-md-inline-block'>&nbsp;- {record.player2.score} pts</span>
                    </td>
                    <td className='d-none d-md-table-cell'>
                        <img src={`${getGameAssetsUrl(1)}/icon.png`} alt="Pong" height={24} />
                    </td>
                    <td>
                        <span className='d-none d-md-inline-block'>
                            {new Date(record.recordDate).toLocaleString()}
                        </span>
                        <span className='d-inline-block d-md-none'>
                            {new Date(record.recordDate).toLocaleDateString()}
                        </span>
                    </td>
                    <td className="d-none d-md-table-cell">
                        {record.winner.name}
                    </td>
                </tr>
            )
        })}</>;
    }



    return (<>
        <Helmet>
            <title>
                {`Darknet Games | ${profile && profile.name}'s Profile`}
            </title>
        </Helmet>
        <Container className="profile">
            <Row>
                <Col>
                    <Card className="card-widget" id="profile">
                        <Card.Header>
                            <Row className="justify-content-between">
                                <Col xs={7} md={10}>
                                    <div className="d-none d-md-block">
                                        <CustomBreadcrumbs steps={['profile']} /> {profile && (<>&gt; {profile.address}</>)}
                                    </div>
                                    <div className="d-block d-md-none">
                                        <CustomBreadcrumbs steps={['profile']} /> &gt; {profile && shortenAddress(profile.address)}
                                    </div>
                                </Col>
                                <Col xs={5} md={2} className="text-end">
                                    <Link to={`https://explorer.solana.com/address/${address}`} target="_blank" rel="noreferrer noopener" title="Solana Explorer" className="me-3">
                                        <CustomTooltip id={"solana-explorer"} title={"Solana Explorer"} placement={"top"}>
                                            <img src={solanaIcon} alt="solana" height="16" />
                                        </CustomTooltip>
                                    </Link>
                                    {profile && address === identity.account.address && <>
                                        <EditProfileModal profile={profile} updateProfile={setProfile} />
                                    </>}
                                </Col>
                            </Row>
                        </Card.Header>
                        <Card.Body>
                            <Profile />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    </>);
}

export default ProfilePage;